import { useEffect, useState } from "react";
import { Loader, Paperclip, Play } from "lucide-react";
import { useToast } from "../Auth-Context/ToastContext";
import {
  playInteractionRecording,
  downloadInteractionAttachment,
} from "../api/services";

// Blob requests return errors as a Blob too, so read the backend's
// `message` out of it before showing it to the user.
async function getErrorMessage(err, fallback) {
  const data = err?.response?.data;
  try {
    if (data instanceof Blob) {
      const parsed = JSON.parse(await data.text());
      return parsed?.message || fallback;
    }
  } catch (_e) {
    /* fall through to the fallback below */
  }
  if (data?.message) return data.message;
  if (err?.request) return "Network error. Please check your connection and try again.";
  return fallback;
}

const formatDuration = (seconds) => {
  const total = Math.max(0, Math.floor(Number(seconds) || 0));
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, "0")}`;
};

/* ── Call recording: loads only when the user clicks play ── */
export function RecordingPlayer({ interaction }) {
  const toast = useToast();
  const [url, setUrl] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    return () => {
      if (url) URL.revokeObjectURL(url);
    };
  }, [url]);

  if (!interaction?.hasRecording) return null;

  const load = async () => {
    setLoading(true);
    try {
      const blob = await playInteractionRecording({ id: interaction.id });
      setUrl(URL.createObjectURL(blob));
    } catch (err) {
      toast.error(await getErrorMessage(err, "Could not load the recording."));
    } finally {
      setLoading(false);
    }
  };

  if (url) {
    return <audio controls autoPlay src={url} className="w-full h-8 mt-1.5 max-w-[320px]" />;
  }

  return (
    <button
      type="button"
      onClick={load}
      disabled={loading}
      className="inline-flex items-center gap-1.5 mt-1.5 px-2.5 h-7 rounded-lg text-[11px] font-semibold border border-neutral-200 text-[#009DB9] hover:bg-neutral-50 transition-colors disabled:opacity-60"
    >
      {loading ? <Loader size={11} className="animate-spin" /> : <Play size={11} />}
      {loading
        ? "Loading recording…"
        : `Play recording${interaction.recordingDurationSeconds != null ? ` (${formatDuration(interaction.recordingDurationSeconds)})` : ""}`}
    </button>
  );
}

/* ── PDF attachment: opens in a new tab ── */
export function AttachmentLink({ interaction }) {
  const toast = useToast();
  const [loading, setLoading] = useState(false);

  if (!interaction?.attachmentName) return null;

  const open = async () => {
    // Open the tab right away (inside the click) so pop-up blockers allow it.
    const win = window.open("", "_blank");
    setLoading(true);
    try {
      const blob = await downloadInteractionAttachment({ id: interaction.id });
      const url = URL.createObjectURL(new Blob([blob], { type: "application/pdf" }));
      if (win) {
        win.location.href = url;
      } else {
        const a = document.createElement("a");
        a.href = url;
        a.download = interaction.attachmentName;
        a.click();
      }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      if (win) win.close();
      toast.error(await getErrorMessage(err, "Could not open the attachment."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <button
      type="button"
      onClick={open}
      disabled={loading}
      className="inline-flex items-center gap-1.5 mt-1.5 text-[11px] font-semibold text-[#009DB9] hover:underline disabled:opacity-60"
    >
      {loading ? <Loader size={11} className="animate-spin" /> : <Paperclip size={11} />}
      <span className="truncate max-w-[220px]">{interaction.attachmentName}</span>
    </button>
  );
}

/* ── Email notes: the backend sends a plain-text AI summary, or the HTML
   body if the summariser was off or failed. Show both safely. ── */
const HTML_PATTERN = /<\/?[a-z][\s\S]*>/i;
const ALLOWED_TAGS = new Set(["B", "I", "U", "STRONG", "EM", "UL", "OL", "LI", "DIV", "P", "BR", "SPAN"]);

function sanitizeHtml(html) {
  const doc = new DOMParser().parseFromString(html, "text/html");

  const clean = (node) => {
    [...node.childNodes].forEach((child) => {
      if (child.nodeType === 1) {
        if (!ALLOWED_TAGS.has(child.tagName)) {
          child.replaceWith(document.createTextNode(child.textContent || ""));
        } else {
          [...child.attributes].forEach((a) => child.removeAttribute(a.name));
          clean(child);
        }
      } else if (child.nodeType !== 3) {
        child.remove();
      }
    });
  };

  clean(doc.body);
  return doc.body.innerHTML;
}

export function InteractionNotes({ notes, className = "" }) {
  const text = notes || "";

  if (HTML_PATTERN.test(text)) {
    return (
      <div
        className={`${className} [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5`}
        dangerouslySetInnerHTML={{ __html: sanitizeHtml(text) }}
      />
    );
  }

  return <div className={`${className} whitespace-pre-wrap`}>{text}</div>;
}